'use client';
import { Check, Clock, X } from 'lucide-react';
import type { QuestionAnswerRecord } from '../../../src/shared/wireContract';
import { useTranslation } from '../../lib/i18n';

/** Durable answer record drawn identically after live settlement and history hydration: one quiet line per
 *  question, its header beside the chosen answer, or the permission outcome beside the command it covered. */
export function QuestionAnswerSummary({ record }: { record: QuestionAnswerRecord }) {
  const { t } = useTranslation();
  const answerText = (question: QuestionAnswerRecord['questions'][number]) => {
    switch (question.outcome) {
      case 'once': return t.brainChat.askAllowedOnce;
      case 'conversation': return t.brainChat.ux.approvalConversationOutcome;
      case 'always': return t.brainChat.askAllowedAlways;
      case 'deny': return question.other ? `${t.brainChat.ux.approvalFeedbackOutcome}: ${question.other}` : t.brainChat.askDenied;
      case 'expired': return t.brainChat.askExpiredRecord;
      case 'answered': return [...question.selected, ...(question.other ? [question.other] : [])].join(', ');
    }
  };
  return <div data-testid="question-answer-record" className="my-1 space-y-0.5 text-meta text-muted-foreground">
    {record.questions.map((question, index) => {
      const Icon = question.outcome === 'expired' ? Clock : question.outcome === 'deny' ? X : Check;
      return <p key={index} className="flex min-w-0 items-start gap-2">
        <Icon size={12} aria-hidden className={`mt-1 shrink-0 ${question.outcome === 'deny' ? 'text-destructive' : ''}`} />
        <span className="min-w-0 break-words">
          {question.outcome === 'answered' ? <>{question.header}<span className="mx-1.5 opacity-50">/</span></> : null}
          <span className={question.outcome === 'deny' ? 'text-destructive' : 'text-foreground'}>{answerText(question)}</span>
          {question.approval?.command ? <code className="ml-2 break-all font-mono text-small">$ {question.approval.command}</code> : null}
        </span>
      </p>;
    })}
  </div>;
}
